/**
 * function returns a localized date string from a utc date object
 * @param date utc date
 */
import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import weekOfYear from "dayjs/plugin/weekOfYear";

dayjs.extend(weekOfYear);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(relativeTime);

export function utcDateToLocalDateString(date: Date): string {
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  const dateString = date.toISOString();

  return dateString.substring(0, dateString.length - 1);
}

/**
 * function returns a date object rounded to minutes
 * @param minutes
 * @param d
 */
export function getRoundedDate(minutes: number, d = new Date()): Date {
  const ms = 1000 * 60 * minutes; // convert minutes to ms

  return new Date(Math.round(d.getTime() / ms) * ms);
}

export const formatDate = (
  date: Date | null | undefined,
  format = "D MMM YYYY, HH:mm",
) => {
  if (!date) {
    return "";
  }

  return dayjs(date).format(format);
};

export const dateFrom = (date: Date | null) => {
  if (!date) {
    return "";
  }

  return dayjs(date).fromNow();
};

export const dateToString = (date: Date) => {
  try {
    return date.toString();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);

    return "error";
  }
};

export const formatDateAndTimeToIsoString = (date: string, time: string) => {
  const dateObject = dayjs(date).format("DD/MM/YYYY");

  const formattedDateObject = dayjs(
    `${dateObject} ${time}`,
    "DD/MM/YYYY HH:mm",
  );

  return formattedDateObject.toISOString();
};

/** Returns a cloned Dayjs object with a flag to use UTC time. */
export const dateToUtc = (date: Date) => {
  return dayjs(date).utc().format();
};

export const formKitDate = (date: Date | null | undefined) =>
  formatDate(date, "YYYY-MM-DDTHH:mm");

export const formKitDateNoTime = (date: Date | null | undefined) =>
  formatDate(date, "YYYY-MM-DD");

export const getNowDate = () => dayjs();

export const isSameDay = (date1: Dayjs, date2: Dayjs) => {
  return date1.format("DD-MM-YYYY") === date2.format("DD-MM-YYYY");
};

export const formkitTime = (date: Date) => formatDate(date, "HH:mm");

/** Transforms a date in string form like "4 April 2024" to ISO form "2024-04-04" */
export const stringDateToISO = (date: string) => {
  return dayjs(date, "D MMMM YYYY").format("YYYY-MM-DD");
};

export const dateToDayMonthYearString = (date: string) => {
  return formatDate(new Date(date), "D MMMM YYYY");
};
